import React from 'react';
import HeaderFooterShell from '@hydra/common/shell/HeaderFooterShell';
import { useTheme } from '@material-ui/core/styles';
import settings from '@hydra/utils/settings';
import { BrowserRouter } from 'react-router-dom';
import { Link } from '../components/Link';

import {
  Provider as AppBridgeProvider,
  useAppBridge
} from '@shopify/app-bridge-react';
import { AppProvider as PolarisProvider, Frame } from '@shopify/polaris';
import { authenticatedFetch } from '@shopify/app-bridge-utils';
import { LinkLikeComponent } from '@shopify/polaris/build/ts/latest/src/utilities/link';
import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache
} from '@apollo/client';
import { Redirect } from '@shopify/app-bridge/actions';
import { ProjectProvider } from '../components/Providers/ProjectProvider';
import { Routes } from '../components/Routes';
import { SWRConfig } from 'swr';

import '@shopify/polaris/build/esm/styles.css';
import { AppBridgeState, ClientApplication } from '@shopify/app-bridge';
import ErrorDisplay from '../components/Providers/ErrorDisplay';
import { UserProvider } from '../components/Providers/UserProvider';

export default function ShopifyContainer () {
  const theme = useTheme();
  const translations = {};

  const shopifyHostId = new URL(location.toString()).searchParams.get('host') || '';
  const shopUrl = new URL(location.toString()).searchParams.get('shop') || '';

  return (
    <HeaderFooterShell
      hideFooter
      hideHeader
      bgColor={theme.palette.background.secondary}
    >
      <SWRConfig
        value={{
          revalidateOnFocus: false
        }}
      >
        <BrowserRouter>
          <PolarisProvider i18n={translations} linkComponent={Link as LinkLikeComponent}>
            <AppBridgeProvider
              config={{
                apiKey: settings.get('shopifyPublicApiKey'),
                host: shopifyHostId,
                forceRedirect: true
              }}
            >
              <Frame>
                <ConfiguredApolloProvider>
                  <UserProvider shopifySiteName={shopUrl}>
                    <ErrorDisplay>
                      <ProjectProvider url={shopUrl}>
                        <Routes />
                      </ProjectProvider>
                    </ErrorDisplay>
                  </UserProvider>
                </ConfiguredApolloProvider>
              </Frame>
            </AppBridgeProvider>
          </PolarisProvider>
        </BrowserRouter>
      </SWRConfig>
    </HeaderFooterShell>
  );
}

function ConfiguredApolloProvider ({ children }: { children: React.ReactNode}) {
  const app = useAppBridge();

  const loggedInFetch = userLoggedInFetch(app);
  if (loggedInFetch === null) {
    return <>Redirecting in this weird place</>;
  }
  const client = new ApolloClient({
    cache: new InMemoryCache(),
    uri: '/shopify/graphql',
    link: new HttpLink({
      credentials: 'include',
      fetch: loggedInFetch
    })
  });

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}

export function userLoggedInFetch (app: ClientApplication<AppBridgeState>) {
  const fetchFunction = authenticatedFetch(app);

  return async (uri: string, options: any) => {
    const response = await fetchFunction(uri, options);
    console.log(JSON.stringify(response.body, null, 2));

    if (
      response.headers.get('X-Shopify-API-Request-Failure-Reauthorize') === '1'
    ) {
      const authUrlHeader = response.headers.get(
        'X-Shopify-API-Request-Failure-Reauthorize-Url'
      );

      const redirect = Redirect.create(app);
      redirect.dispatch(Redirect.Action.APP, authUrlHeader || '/shopify/auth');
      return response; // this used to be null
    }

    return response;
  };
}
